import type { Component } from '../schema';
import { convertTax } from './tax';

/**
 * How the embedding shop wants prices: on its tax basis and in its currency.
 *
 * One Component can run in several shops at once — one showing gross prices
 * in EUR, a B2B shop showing net prices, a Swiss shop in CHF. The Component
 * keeps the prices it was authored with; every amount the visitor sees or the
 * host receives goes through {@link toHostAmount} with the shop's settings.
 */
export interface HostPricing {
    /**
     * The shop's display tax rate in percent: `0` for a net-display shop, the
     * product's rate for a gross-display shop. `undefined` = no host (admin
     * preview) → the Component's own basis.
     */
    storeTaxRate?: number | undefined;
    /** ISO code of the currency the shop displays (e.g. `CHF`). */
    displayCurrency?: string | undefined;
    /**
     * Factor from a currency into {@link displayCurrency}, keyed by ISO code
     * (`{ "EUR": 0.94 }` = 1 EUR is 0.94 CHF). The shop's own rates, so the
     * configured amount moves with the product price when the merchant
     * updates them.
     */
    currencyRates?: Readonly<Record<string, number>> | undefined;
}

export interface ResolvedCurrency {
    /** Currency every converted amount is denominated in. */
    currency: string;
    /** Factor applied after the tax conversion. `1` = no conversion. */
    rate: number;
}

/**
 * The currency to show and the factor to get there.
 *
 * Without a usable rate the Component's currency stays — a correctly labelled
 * EUR amount is better than a CHF label on an EUR number.
 */
export function resolveCurrency(componentCurrency: string, host: HostPricing | undefined): ResolvedCurrency {
    const target = host?.displayCurrency?.trim().toUpperCase();
    const source = componentCurrency.toUpperCase();
    if (!target || target === source) return { currency: componentCurrency, rate: 1 };

    const rate = host?.currencyRates?.[source];
    if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) {
        return { currency: componentCurrency, rate: 1 };
    }
    return { currency: target, rate };
}

/** The Component's tax settings as the converter needs them. */
export function componentTaxRate(component: Component): number {
    const raw = component.setting.taxRate;
    if (raw == null || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
}

/**
 * One authored amount on the host's basis: tax first, then currency.
 *
 * Mirrors the Legacy `Justselling_Configento_App_Tax.convert`: as soon as the
 * host names a tax rate, the amount is rebased onto it — also when the rates
 * happen to be equal, because a net-authored Component in a gross shop still
 * has to gain the tax.
 */
export function toHostAmount(
    amount: number,
    component: Component,
    host: HostPricing | undefined,
    currency: ResolvedCurrency,
): number {
    const taxed = convertTax(amount, {
        configuratorTaxRate: componentTaxRate(component),
        priceIncludesTax: component.priceIncludesTax,
        storeTaxRate: host?.storeTaxRate,
    });
    return currency.rate === 1 ? taxed : taxed * currency.rate;
}

/**
 * Whether amounts on the host's basis include tax.
 *
 * Follows the basis the amounts were converted TO, not the authoring flag:
 * a net-authored Component in a gross shop yields gross amounts, and a host
 * that takes the flag at face value would add the tax a second time.
 */
export function hostPriceIncludesTax(component: Component, host: HostPricing | undefined): boolean {
    const rate = host?.storeTaxRate;
    if (rate === undefined || !Number.isFinite(rate)) return component.priceIncludesTax;
    return rate > 0;
}
